import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePodcastStore } from '../../stores/podcastStore';
import PodcastAudioEngine from '../live/PodcastAudioEngine';

const formatTime = (seconds) => {
  if (isNaN(seconds)) return '00:00';
  const date = new Date(seconds * 1000);
  const mm = date.getUTCMinutes();
  const ss = date.getUTCSeconds().toString().padStart(2, '0');
  return `${mm}:`;
};

const MiniAudioPlayer = () => {
  const location = useLocation();
  const currentPodcast = usePodcastStore((state) => state.currentPodcast);
  const isPlaying = usePodcastStore((state) => state.isPlaying);
  const togglePlay = usePodcastStore((state) => state.togglePlay);
  const currentTime = usePodcastStore((state) => state.currentTime);
  const duration = usePodcastStore((state) => state.duration);
  const setCurrentTime = usePodcastStore((state) => state.setCurrentTime);
  const volume = usePodcastStore((state) => state.volume);
  const setVolume = usePodcastStore((state) => state.setVolume);

  // 🟢 Lógica de Visibilidad:
  // 1. Si no hay podcast, solo renderizamos el motor (que no hará nada) y retornamos null para la UI.
  // 2. Si estamos en la página del podcast full (/podcast/:id), ocultamos el mini player para no duplicar controles.
  //    Excluimos '/podcast/upload' de esta regla.
  const isFullPlayerPage = location.pathname.startsWith('/podcast/') && location.pathname !== '/podcast/upload';
  
  if (!currentPodcast) return <PodcastAudioEngine />;

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const handleSeek = (e) => {
    const newTime = (e.target.value / 100) * duration;
    setCurrentTime(newTime);
  };

  return (
    <>
      {/* Motor de Audio (Invisible) */}
      <PodcastAudioEngine />
      
      {!isFullPlayerPage && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/90 border-t border-[#D4AF37]/20 px-4 py-3 z-50 flex items-center justify-between backdrop-blur-md shadow-[0_-5px_20px_rgba(0,0,0,0.8)]">
      
      {/* Info del Podcast */}
      <div className="flex items-center w-1/4 min-w-[200px]">
        <img 
          src={currentPodcast.coverImage} 
          alt={currentPodcast.title} 
          className="w-12 h-12 rounded-md object-cover mr-3 border border-[#D4AF37]/20 shadow-sm"
        />
        <div className="overflow-hidden">
          <h4 className="text-white font-semibold text-sm truncate">{currentPodcast.title}</h4>
          <p className="text-gray-400 text-xs truncate">
            {currentPodcast.author?.username || currentPodcast.authorDetails?.username || 'Ágora Creator'}
          </p>
        </div>
      </div>

      {/* Controles y Progreso */}
      <div className="flex flex-col items-center flex-1 px-4 max-w-2xl">
        <div className="flex items-center space-x-6 mb-1">
          <button className="text-gray-400 hover:text-[#D4AF37] transition-colors">
            <i className="fas fa-step-backward"></i>
          </button>
          <button 
            onClick={togglePlay}
            className="w-8 h-8 bg-[#D4AF37] text-black rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-[0_0_10px_rgba(212,175,55,0.4)]"
          >
            <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'} text-xs`}></i>
          </button>
          <button className="text-gray-400 hover:text-[#D4AF37] transition-colors">
            <i className="fas fa-step-forward"></i>
          </button>
        </div>
        
        <div className="w-full flex items-center space-x-3 text-xs text-gray-400">
          <span>{formatTime(currentTime)}</span>
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={progress || 0} 
            onChange={handleSeek}
            className="flex-1 h-1 bg-gray-800 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-[#D4AF37] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-[0_0_5px_rgba(212,175,55,0.5)]"
          />
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Volumen y Opciones */}
      <div className="flex items-center justify-end w-1/4 space-x-4">
        <div className="flex items-center group">
          <i className="fas fa-volume-up text-gray-400 mr-2 text-xs group-hover:text-[#D4AF37] transition-colors"></i>
          <input 
            type="range" 
            min="0" 
            max="1" 
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-20 h-1 bg-gray-800 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-gray-400 [&::-webkit-slider-thumb]:rounded-full hover:[&::-webkit-slider-thumb]:bg-[#D4AF37]"
          />
        </div>
        <Link to={`/podcast/${currentPodcast._id}`} className="text-gray-400 hover:text-[#D4AF37] transition-colors" title="Pantalla Completa">
          <i className="fas fa-expand-alt"></i>
        </Link>
      </div>
        </div>
      )}
    </>
  );
};

export default MiniAudioPlayer;
